import React from 'react';
import { Dropdown, DropdownButton, Spinner } from 'react-bootstrap';

type PurchaseOrderActionsProps = {
  status: string;
  canApprove?: boolean;
  canDelete?: boolean;
  isAdmin?: boolean;
  hasPendingSuggestions?: boolean;
  addPurchaseOrderItem?: () => void;
  uploadListPhoto?: () => void;
  sendForApproval?: () => void;
  markAsNew?: () => void;
  approveRequest?: () => void;
  confirmRequest?: () => void;
  closeOrder?: () => void;
  cancelOrder?: () => void;
  requestForDeletion?: () => void;
  download?: () => void;
  deletePurchaseOrder?: () => void;
  onRerunListExtraction?: () => void;
  onRematchProducts?: () => void;
  onRerunOrderingOpinions?: () => void;
  isQueueingAiJob?: boolean;
};

const PurchaseOrderActions = ({
  status,
  canApprove = false,
  canDelete = false,
  isAdmin = false,
  hasPendingSuggestions = false,
  addPurchaseOrderItem,
  uploadListPhoto,
  sendForApproval,
  markAsNew,
  approveRequest,
  confirmRequest,
  closeOrder,
  cancelOrder,
  requestForDeletion,
  download,
  deletePurchaseOrder,
  onRerunListExtraction,
  onRematchProducts,
  onRerunOrderingOpinions,
  isQueueingAiJob = false
}: PurchaseOrderActionsProps) => {
  const canAddItem = isAdmin || status === 'New' || status === 'Future';
  const addItemAction = canAddItem ? (
    <Dropdown.Item onClick={addPurchaseOrderItem}>Add Item</Dropdown.Item>
  ) : null;

  const renderDropdownItems = () => {
    switch (status) {
      case 'New':
        return (
          <>
            {addItemAction}
            <Dropdown.Item onClick={uploadListPhoto}>
              Upload list photo
            </Dropdown.Item>
            <Dropdown.Item
              onClick={sendForApproval}
              disabled={hasPendingSuggestions}
              title={
                hasPendingSuggestions
                  ? 'Resolve product suggestions before sending for approval'
                  : undefined
              }
            >
              Send for approval
            </Dropdown.Item>
            <Dropdown.Item onClick={requestForDeletion}>
              Request for deletion
            </Dropdown.Item>
            <Dropdown.Item onClick={cancelOrder}>Cancel order</Dropdown.Item>
          </>
        );
      case 'Future':
        return (
          <>
            {addItemAction}
            <Dropdown.Item onClick={markAsNew}>Mark as New</Dropdown.Item>
            <Dropdown.Item onClick={uploadListPhoto}>
              Upload list photo
            </Dropdown.Item>
            <Dropdown.Item onClick={requestForDeletion}>
              Request for deletion
            </Dropdown.Item>
            <Dropdown.Item onClick={cancelOrder}>Cancel order</Dropdown.Item>
          </>
        );
      case 'Analyzing':
        return (
          <>
            {addItemAction}
            <Dropdown.Item disabled>
              AI analysis in progress…
            </Dropdown.Item>
            <Dropdown.Item onClick={requestForDeletion}>
              Request for deletion
            </Dropdown.Item>
          </>
        );
      case 'ForDeletion':
        return (
          <>
            {addItemAction}
            {canDelete ? (
              <Dropdown.Item onClick={deletePurchaseOrder}>Delete</Dropdown.Item>
            ) : (
              <Dropdown.Item disabled>Awaiting admin deletion</Dropdown.Item>
            )}
          </>
        );
      case 'AwaitingApproval':
        return (
          <>
            {addItemAction}
            <Dropdown.Item onClick={download}>Generate PDF</Dropdown.Item>
            {canApprove && (
              <Dropdown.Item onClick={approveRequest}>Approve</Dropdown.Item>
            )}
            <Dropdown.Item onClick={requestForDeletion}>
              Request for deletion
            </Dropdown.Item>
          </>
        );
      case 'Approved':
        return (
          <>
            {addItemAction}
            <Dropdown.Item onClick={download}>Generate PDF</Dropdown.Item>
            <Dropdown.Item onClick={confirmRequest}>
              Sent To Supplier
            </Dropdown.Item>
            <Dropdown.Item onClick={cancelOrder}>Cancel order</Dropdown.Item>
          </>
        );
      case 'Sent':
        return (
          <>
            {addItemAction}
            <Dropdown.Item onClick={download}>Generate PDF</Dropdown.Item>
            <Dropdown.Item onClick={closeOrder}>Close</Dropdown.Item>
            <Dropdown.Item onClick={cancelOrder}>Cancel order</Dropdown.Item>
          </>
        );
      case 'Cancelled':
        return (
          <>
            {addItemAction}
            <Dropdown.Item disabled>Cancelled</Dropdown.Item>
          </>
        );
      default:
        return (
          <>
            {addItemAction}
            {!addItemAction && (
              <Dropdown.Item>No actions available</Dropdown.Item>
            )}
          </>
        );
    }
  };

  return (
    <DropdownButton title="Actions" variant="phoenix-primary">
      {renderDropdownItems()}
      {isAdmin && (
        <>
          <Dropdown.Divider />
          <Dropdown.Header>AI (debug)</Dropdown.Header>
          <Dropdown.Item onClick={onRerunListExtraction} disabled={isQueueingAiJob}>
            {isQueueingAiJob && <Spinner animation="border" size="sm" className="me-2" />}
            Re-run list extraction
          </Dropdown.Item>
          <Dropdown.Item onClick={onRematchProducts} disabled={isQueueingAiJob}>
            Rematch products
          </Dropdown.Item>
          <Dropdown.Item onClick={onRerunOrderingOpinions} disabled={isQueueingAiJob}>
            Re-run ordering opinions
          </Dropdown.Item>
        </>
      )}
    </DropdownButton>
  );
};

export default PurchaseOrderActions;
